import React, {type JSX} from 'react';
import {useMaskito} from '@maskito/react';
import {maskitoWithPlaceholder} from '@maskito/kit';
import {
  Button,
  CircularProgress,
  FormControl,
  OutlinedInput,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Form, Formik} from 'formik';
import * as yup from 'yup';
import {InputHelperText} from './shared';

type FormValues = {
  code: string;
};

const REFERENCE_CODE_LENGTH = 16;

const REFERENCE_CODE_PLACEHOLDER = '____ ____ ____ ____';

const formInitialValues: FormValues = {
  code: '',
};

const validationSchema = yup.object({
  code: yup.string().trim().required('Reference code is required'),
});

const StyledForm = styled(Form)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 200px',
  gap: `0 ${theme.spacing(2)}`,
}));

const StyledFormControl = styled(FormControl)({
  width: '100%',
});

const StyledInput = styled(OutlinedInput)(({theme}) => ({
  '& input': {
    backgroundColor: theme.palette.common.white,
    fontWeight: 400,
    fontSize: '20px',
    lineHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    height: '68px !important',
    boxSizing: 'border-box',
    textAlign: 'center',
    '&:focus::placeholder': {
      color: 'transparent',
    },
  },
}));

const StyledInputHelperText = styled(InputHelperText)({
  '&.MuiFormHelperText-root': {
    textAlign: 'center',
  },
});

const StyledButton = styled(Button)({
  '&.MuiButton-root': {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    height: '68px',
    width: '100%',
    boxShadow: 'none',
  },
});

const unmaskCode = (value: string): string => value.replaceAll(/[\s_]/g, '');

type Props = {
  errorMessage: string | undefined;
  setErrorMessage: (value: string | undefined) => void;
  authorizeReferenceCode: (code: string) => Promise<void>;
  isAuthorizing: boolean;
};
function ReferenceCodeForm({
  errorMessage,
  setErrorMessage,
  authorizeReferenceCode,
  isAuthorizing,
}: Readonly<Props>): JSX.Element {
  const [displayValue, setDisplayValue] = React.useState('');

  const maskOptions = React.useMemo(
    () => ({
      mask: [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ],
      ...maskitoWithPlaceholder(REFERENCE_CODE_PLACEHOLDER),
    }),
    []
  );

  const maskedInputRef = useMaskito({options: maskOptions});

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={formInitialValues}
      onSubmit={async ({code}) => {
        await authorizeReferenceCode(unmaskCode(code));
      }}
    >
      {formik => (
        <StyledFormControl
          error={Boolean(
            (formik.touched.code && formik.errors.code) || errorMessage || ''
          )}
        >
          <StyledForm>
            <StyledInput
              id="code"
              name="code"
              placeholder="Type reference code here"
              value={displayValue}
              onFocus={() => {
                if (!displayValue) {
                  setDisplayValue(REFERENCE_CODE_PLACEHOLDER);
                }
              }}
              onBlur={() => {
                formik.setFieldTouched('code');
                if (unmaskCode(displayValue).length === 0) {
                  setDisplayValue('');
                }
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value;
                setDisplayValue(newValue);
                formik.setFieldValue('code', unmaskCode(newValue));
                setErrorMessage(undefined);
              }}
              inputProps={{
                'data-testid': 'reference-code-input',
                ref: maskedInputRef,
              }}
            />
            <div>
              <StyledButton
                disabled={
                  formik.values.code.length !== REFERENCE_CODE_LENGTH ||
                  Boolean(errorMessage) ||
                  isAuthorizing
                }
                color="primary"
                variant="contained"
                data-testid="reference-code-submit-button"
                type="submit"
              >
                {isAuthorizing ? <CircularProgress size={25} /> : 'Continue'}
              </StyledButton>
            </div>
            <div>
              <StyledInputHelperText shrink>
                {(formik.touched.code && formik.errors.code) ||
                  errorMessage ||
                  ''}
              </StyledInputHelperText>
            </div>
          </StyledForm>
        </StyledFormControl>
      )}
    </Formik>
  );
}

export default ReferenceCodeForm;
